import React, { useState, useEffect, useCallback, useMemo } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";

const HDPerformanceDashboard = () => {
  const pidRedux = useSelector((state) => state.user.projectId);

  // =========================
  // State Management
  // =========================
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'total_open', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filter States
  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [hdidOptions, setHdidOptions] = useState([]);
  const [hdNameOptions, setHdNameOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [selectedHDID, setSelectedHDID] = useState([]);
  const [selectedHDNames, setSelectedHDNames] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [appliedFilters, setAppliedFilters] = useState({
    orgs: [],
    schemes: [],
    schemeTypes: [],
    hdid: [],
    hdnames: [],
    managers: [],
    startDate: "",
    endDate: "",
  });

  // Info popup state
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  const BASE = "https://api-project360-insights.haqdarshak.com/kpi/hd";
  const BASE_REPORT = "https://api-project360-insights.haqdarshak.com/kpi/hd-report";

  // Definitions for info popups
  const definitions = {
    "Total HDs": {
      title: "Total HDs",
      description: "Total number of Haqdarshaks (HDs) who are working on this project. This includes both active and inactive HDs.",
      example: "Total HDs = Active HDs + Inactive HDs\nExample: 100 = 85 + 15"
    },
    "Active HDs": {
      title: "Active HDs",
      description: "Number of Haqdarshaks who are currently active and working on the project. Active HDs are those who are actively processing applications.",
    },
    "Inactive HDs": {
      title: "Inactive HDs",
      description: "Number of Haqdarshaks who are currently inactive. These HDs may have been removed from the project or are temporarily not working.",
    },
    "Total Opened": {
      title: "Total Opened Applications",
      description: "Total number of applications that have been opened by HDs across all schemes and states. This includes all applications regardless of their current status.",
      example: "Total Opened = Open + Submitted + Benefit Received + Aborted\nExample: 1000 = 400 + 300 + 200 + 100"
    },
    "Total BR": {
      title: "Total Benefit Received",
      description: "Total number of applications where the benefit has been received and verified. These are applications where the proof of benefit has been uploaded on the app.",
    },
    "Total DS": {
      title: "Total Docket Submitted",
      description: "Total number of applications where the docket has been submitted. These are applications where the proof of submission has been uploaded.",
    },
    "Total Aborted": {
      title: "Total Aborted Applications",
      description: "Total number of applications that were started but aborted either due to incorrect internal records or during internal quality checks.",
    }
  };

  // =========================
  // Helper Functions
  // =========================
  const buildOptions = (fetchedValues, selectedValues) => {
    const fetched = fetchedValues.map((v) => ({ value: v, label: v }));
    const selectedOnly = selectedValues
      .filter((v) => !fetchedValues.includes(v))
      .map((v) => ({ value: v, label: v }));
    return [...selectedOnly, ...fetched];
  };

  const getFilterQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    selectedOrgs.forEach((o) => params.append("OrgName", o));
    selectedSchemes.forEach((s) => params.append("SchemeName", s));
    selectedSchemeTypes.forEach((t) => params.append("SchemeType", t));
    selectedHDID.forEach((h) => params.append("HDID", h));
    selectedHDNames.forEach((n) => params.append("haqdarshakName", n));
    selectedManagers.forEach((m) => params.append("ManagerName", m));
    if (pidRedux) params.append("pid", pidRedux);
    return params.toString();
  }, [selectedOrgs, selectedSchemes, selectedSchemeTypes, selectedHDID, selectedHDNames, selectedManagers, pidRedux]);

  const getDashboardQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    appliedFilters.orgs.forEach((o) => params.append("OrgName", o));
    appliedFilters.schemes.forEach((s) => params.append("SchemeName", s));
    appliedFilters.schemeTypes.forEach((t) => params.append("SchemeType", t));
    appliedFilters.hdid.forEach((h) => params.append("HDID", h));
    appliedFilters.hdnames.forEach((n) => params.append("haqdarshakName", n));
    appliedFilters.managers.forEach((m) => params.append("ManagerName", m));
    if (pidRedux) params.append("pid", pidRedux);
    if (appliedFilters.startDate) params.append("start_date", appliedFilters.startDate);
    if (appliedFilters.endDate) params.append("end_date", appliedFilters.endDate);
    return params.toString();
  }, [appliedFilters, pidRedux]);

  // =========================
  // Fetch Functions
  // =========================
  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = getFilterQueryParams();
      const managerParams = new URLSearchParams();
      if (pidRedux) managerParams.append("pid", pidRedux);

      const [orgRes, schemeRes, typeRes, hdidRes, hdnameRes, managerRes] = await Promise.all([
        fetch(`${BASE}/unique-org-name/?${params}`),
        fetch(`${BASE}/unique-scheme/?${params}`),
        fetch(`${BASE}/unique-type/?${params}`),
        fetch(`${BASE}/unique-hd-id/?${params}`),
        fetch(`${BASE}/unique-hd-name/?${params}`),
        // Manager Name comes from the hd_master_report table (HD roster),
        // not the case-level combined table, so it has its own endpoint.
        fetch(`${BASE_REPORT}/managers/?${managerParams.toString()}`),
      ]);

      const orgData = await orgRes.json();
      const schemeData = await schemeRes.json();
      const typeData = await typeRes.json();
      const hdidData = await hdidRes.json();
      const hdnameData = await hdnameRes.json();
      const managerData = await managerRes.json();

      setOrgOptions(buildOptions(orgData.unique_Organizations ?? [], selectedOrgs));
      setSchemeOptions(buildOptions(schemeData.unique_Schemes ?? [], selectedSchemes));
      setSchemeTypeOptions(buildOptions(typeData.unique_Scheme_Types ?? [], selectedSchemeTypes));
      setHdidOptions(buildOptions(hdidData.unique_HDID ?? [], selectedHDID));
      setHdNameOptions(buildOptions(hdnameData.unique_HaqdarshakNames ?? [], selectedHDNames));
      setManagerOptions(
        buildOptions((managerData.Manager_Name ?? []).filter(Boolean), selectedManagers)
      );
    } catch (e) {
      console.error("Filter fetch error", e);
    }
  }, [getFilterQueryParams, selectedOrgs, selectedSchemes, selectedSchemeTypes, selectedHDID, selectedHDNames, selectedManagers, pidRedux]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = getDashboardQueryParams();
      const response = await fetch(`${BASE}/performance-dashboard/?${params}`);
      const data = await response.json();
      setDashboardData(data);
    } catch (e) {
      console.error("Dashboard fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [getDashboardQueryParams]);

  // =========================
  // Sorting and Filtering
  // =========================
  const sortedAndFilteredData = useMemo(() => {
    if (!dashboardData?.hd_list) return [];

    let filtered = dashboardData.hd_list;
    
    // Filter by status - now comparing with 'Active' and 'Inactive' strings
    if (statusFilter !== "all") {
      filtered = filtered.filter(item => {
        const isActive = item.is_active === 'Active';
        return statusFilter === "active" ? isActive : !isActive;
      });
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        String(item.HDID || '').toLowerCase().includes(term) ||
        String(item.AMS_ID || '').toLowerCase().includes(term) ||
        String(item.HDName || '').toLowerCase().includes(term) ||
        String(item.haqdarshakName || '').toLowerCase().includes(term) ||
        String(item.PID || '').toLowerCase().includes(term) ||
        String(item.ProjectName || '').toLowerCase().includes(term) ||
        String(item.HD_Operating_State || '').toLowerCase().includes(term) ||
        String(item.HD_Operating_District || '').toLowerCase().includes(term) ||
        String(item.Manager_Name || '').toLowerCase().includes(term) ||
        (item.scheme_breakdown || []).some((s) =>
          String(s.SchemeName || '').toLowerCase().includes(term)
        )
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key] ?? 0;
        let bVal = b[sortConfig.key] ?? 0;
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [dashboardData, searchTerm, sortConfig, statusFilter]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedAndFilteredData.slice(start, end);
  }, [sortedAndFilteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedAndFilteredData.length / itemsPerPage);

  // =========================
  // Event Handlers
  // =========================
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleExpand = (hdid) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(hdid)) {
        next.delete(hdid);
      } else {
        next.add(hdid);
      }
      return next;
    });
  };

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    setStartDate(val);
    if (val && !endDate) {
      setDateError("Please also select an End Date.");
    } else if (!val && endDate) {
      setDateError("Please also select a Start Date.");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    setEndDate(val);
    if (val && !startDate) {
      setDateError("Please also select a Start Date.");
    } else if (!val && startDate) {
      setDateError("Please also select an End Date.");
    } else {
      setDateError("");
    }
  };

  const applyFilters = () => {
    if (startDate && !endDate) {
      setDateError("End Date is required when Start Date is selected.");
      return;
    }
    if (!startDate && endDate) {
      setDateError("Start Date is required when End Date is selected.");
      return;
    }
    setDateError("");
    setAppliedFilters({
      orgs: selectedOrgs,
      schemes: selectedSchemes,
      schemeTypes: selectedSchemeTypes,
      hdid: selectedHDID,
      hdnames: selectedHDNames,
      managers: selectedManagers,
      startDate,
      endDate,
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedSchemes([]);
    setSelectedSchemeTypes([]);
    setSelectedHDID([]);
    setSelectedHDNames([]);
    setSelectedManagers([]);
    setStartDate("");
    setEndDate("");
    setDateError("");
    setSearchTerm("");
    setStatusFilter("all");
    setAppliedFilters({
      orgs: [],
      schemes: [],
      schemeTypes: [],
      hdid: [],
      hdnames: [],
      managers: [],
      startDate: "",
      endDate: "",
    });
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    if (!dashboardData?.hd_list) return;
    
    const headers = [
      'HDID', 'AMSID', 'HD Name', 'Gender', 'Onboarded Date',
      'Operating State', 'Operating District', 'Age', 'Manager/Opsco Name', 'Scheme Name',
      'Status', 'Opened', 'Benefit Received', 'Submitted', 'Aborted'
    ];

    const escapeCsv = (val) => {
      const str = String(val ?? '');
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    // One CSV row per HD *per scheme* — so the numbers are actually
    // distributed by scheme instead of one row per HD with a flat,
    // comma-joined scheme list. HDs with no scheme breakdown data fall
    // back to a single row using the HD-level totals.
    const csvData = [];
    dashboardData.hd_list.forEach(item => {
      const baseRow = [
        item.HDID || '',
        item.AMS_ID || '',
        item.HDName || '',
        item.HDGender || '',
        item.HDCreatedDate ? new Date(item.HDCreatedDate).toLocaleDateString() : '',
        item.HD_Operating_State || '',
        item.HD_Operating_District || '',
        item.Age || '',
        item.Manager_Name || '',
      ];

      const schemes = item.scheme_breakdown && item.scheme_breakdown.length > 0
        ? item.scheme_breakdown
        : [{
            SchemeName: '',
            total_open: item.total_open,
            total_br: item.total_br,
            total_ds: item.total_ds,
            total_aborted: item.total_aborted,
          }];

      schemes.forEach(scheme => {
        csvData.push([
          ...baseRow,
          scheme.SchemeName || '',
          item.is_active || 'Inactive',  // Now returns 'Active' or 'Inactive'
          scheme.total_open || 0,
          scheme.total_br || 0,
          scheme.total_ds || 0,
          scheme.total_aborted || 0
        ]);
      });
    });

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(escapeCsv).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hd_performance_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // =========================
  // Info Button Component
  // =========================
  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  // =========================
  // Info Popup Component
  // =========================
  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
        onClick={() => setInfoPopup({ show: false, title: '', description: '', example: '' })}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-pink-600 to-purple-600 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={() => setInfoPopup({ show: false, title: '', description: '', example: '' })}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl border border-pink-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-pink-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setInfoPopup({ show: false, title: '', description: '', example: '' })}
                className="px-5 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // =========================
  // Effects
  // =========================
  useEffect(() => {
    const t = setTimeout(() => {
      fetchFilterOptions();
    }, 200);
    return () => clearTimeout(t);
  }, [selectedOrgs, selectedSchemes, selectedSchemeTypes, selectedHDID, selectedHDNames, selectedManagers, pidRedux]);

  useEffect(() => {
    fetchDashboardData();
  }, [appliedFilters, pidRedux]);

  // =========================
  // Rendering Components
  // =========================
  const MultiSelect = ({ options, selected, setSelected, placeholder, label }) => (
    <div className="relative">
      <div className="flex items-center mb-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <InfoButton title={label} />
      </div>
      <Select
        isMulti
        options={options}
        value={options.filter((o) => selected.includes(o.value))}
        onChange={(opts) => setSelected(opts ? opts.map((o) => o.value) : [])}
        placeholder={placeholder}
        closeMenuOnSelect={false}
        className="text-sm"
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '38px',
            borderColor: '#d1d5db',
            '&:hover': { borderColor: '#9ca3af' }
          }),
          option: (base) => ({
            ...base,
            fontSize: '13px'
          })
        }}
      />
    </div>
  );

  const SpinnerIcon = () => (
    <svg className="h-8 w-8 animate-spin text-pink-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <SpinnerIcon />
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Define columns
  const columns = [
    { key: 'HDID', label: 'HD ID' },
    { key: 'AMS_ID', label: 'AMS ID' },
    { key: 'HDName', label: 'HD Name' },
    { key: 'HDGender', label: 'Gender' },
    { key: 'HDCreatedDate', label: 'Onboarded Date' },
    { key: 'HD_Operating_State', label: 'State' },
    { key: 'HD_Operating_District', label: 'District' },
    { key: 'Age', label: 'Age' },
    { key: 'Manager_Name', label: 'Manager/Opsco Name' },
    { key: 'is_active', label: 'Status' },
    { key: 'total_open', label: 'Opened' },
    { key: 'total_br', label: 'BR' },
    { key: 'total_ds', label: 'DS' },
    { key: 'total_aborted', label: 'Aborted' },
  ];

  return (
    <div className="w-[90vw] border-2 border-gray-300 shadow-md rounded-md p-4">
      
      {/* HEADER */}
      <div className="bg-gray-50 px-4 md:px-6 py-4 border rounded-lg mb-4">
        <h2 className="text-xl md:text-xl font-bold text-pink-800">
          HD Performance Dashboard
        </h2>
        <p className="text-sm text-pink-800 mt-1">
          HD-wise performance metrics and aggregated data
        </p>
      </div>

      {/* Summary Cards */}
      {dashboardData?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
          <div className="bg-white p-4 rounded-lg shadow border relative">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Total HDs</p>
              <InfoButton title="Total HDs" />
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1 text-pink-600">
              {dashboardData.summary.total_hd || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border relative">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Active</p>
              <InfoButton title="Active HDs" />
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1 text-green-600">
              {dashboardData.summary.active_hd || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border relative">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Inactive</p>
              <InfoButton title="Inactive HDs" />
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1 text-red-600">
              {dashboardData.summary.inactive_hd || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border relative">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Opened</p>
              <InfoButton title="Total Opened" />
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1 text-blue-600">
              {dashboardData.summary.total_open_applications || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border relative">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">BR</p>
              <InfoButton title="Total BR" />
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1 text-green-600">
              {dashboardData.summary.total_br_count || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border relative">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">DS</p>
              <InfoButton title="Total DS" />
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1 text-purple-600">
              {dashboardData.summary.total_ds_submitted || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border relative">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Aborted</p>
              <InfoButton title="Total Aborted" />
            </div>
            <p className="text-xl md:text-2xl font-bold mt-1 text-red-600">
              {dashboardData.summary.total_aborted_count || 0}
            </p>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <MultiSelect
            options={orgOptions}
            selected={selectedOrgs}
            setSelected={setSelectedOrgs}
            placeholder="Select org/workspace"
            label="Organization"
          />
          <MultiSelect
            options={schemeOptions}
            selected={selectedSchemes}
            setSelected={setSelectedSchemes}
            placeholder="Select service name"
            label="Scheme Name"
          />
          <MultiSelect
            options={schemeTypeOptions}
            selected={selectedSchemeTypes}
            setSelected={setSelectedSchemeTypes}
            placeholder="Select service type"
            label="Service Type"
          />
          <MultiSelect
            options={hdidOptions}
            selected={selectedHDID}
            setSelected={setSelectedHDID}
            placeholder="Select HD ID"
            label="HD ID"
          />
          <MultiSelect
            options={hdNameOptions}
            selected={selectedHDNames}
            setSelected={setSelectedHDNames}
            placeholder="Select HD name"
            label="Haqdarshak Name"
          />
          <MultiSelect
            options={managerOptions}
            selected={selectedManagers}
            setSelected={setSelectedManagers}
            placeholder="Select manager/opsco name"
            label="Manager/Opsco Name"
          />

          <div className="relative">
            <div className="flex items-center mb-1">
              <label className="text-sm font-medium text-gray-700">Status</label>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div className="relative">
            <div className="flex items-center mb-1">
              <label className="text-sm font-medium text-gray-700">📅 Start Date</label>
            </div>
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 ${
                dateError && !startDate ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>
          
          <div className="relative">
            <div className="flex items-center mb-1">
              <label className="text-sm font-medium text-gray-700">📅 End Date</label>
            </div>
            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              min={startDate || undefined}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 ${
                dateError && !endDate ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>

          <div className="flex flex-col gap-2 items-start justify-end">
            {dateError && (
              <p className="text-red-500 text-xs font-medium">⚠ {dateError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition text-sm font-medium"
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition text-sm font-medium shadow-md hover:shadow-lg"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by HD ID, AMS ID, Name, Manager/Opsco, Scheme, State, or District..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
            />
          </div>
          <div className="text-sm text-gray-500 font-medium">
            Found {sortedAndFilteredData.length} records
          </div>
          <button
            onClick={downloadCSV}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg transition flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export CSV
          </button>
          <button
            onClick={fetchDashboardData}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 border-b border-gray-200 w-8"></th>
                {columns.map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors border-b border-gray-200"
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {sortConfig.key === key && (
                        <span className="text-pink-500">
                          {sortConfig.direction === 'asc' ? 
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg> : 
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          }
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => {
                  const schemes = item.scheme_breakdown || [];
                  const isExpandable = schemes.length > 1;
                  const isExpanded = expandedRows.has(item.HDID);

                  return (
                    <React.Fragment key={item.HDID || index}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 py-3 border-r border-gray-100 text-center">
                          {isExpandable && (
                            <button
                              onClick={() => toggleExpand(item.HDID)}
                              className="w-5 h-5 inline-flex items-center justify-center rounded hover:bg-pink-100 text-pink-600 font-bold transition-colors"
                              aria-label={isExpanded ? 'Collapse scheme breakdown' : 'Expand scheme breakdown'}
                              title={`View ${schemes.length} schemes for this HD`}
                            >
                              {isExpanded ? '−' : '+'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-100">{item.HDID}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-r border-gray-100">{item.AMS_ID}</td>
                        <td className="px-4 py-3 text-sm text-gray-800 font-medium border-r border-gray-100">{item.HDName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-r border-gray-100">{item.HDGender}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-r border-gray-100">
                          {item.HDCreatedDate ? new Date(item.HDCreatedDate).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-r border-gray-100">{item.HD_Operating_State}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-r border-gray-100">{item.HD_Operating_District}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-r border-gray-100 text-center">{item.Age || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 border-r border-gray-100">{item.Manager_Name}</td>
                        <td className="px-4 py-3 text-sm border-r border-gray-100">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                            item.is_active === 'Active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {item.is_active || 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm border-r border-gray-100">
                          <span className="inline-flex px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                            {item.total_open || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm border-r border-gray-100">
                          <span className="inline-flex px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                            {item.total_br || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm border-r border-gray-100">
                          <span className="inline-flex px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                            {item.total_ds || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                            {item.total_aborted || 0}
                          </span>
                        </td>
                      </tr>

                      {isExpanded && schemes.map((scheme, sIdx) => (
                        <tr key={`${item.HDID || index}-scheme-${sIdx}`} className="bg-pink-50/50">
                          <td className="border-r border-gray-100"></td>
                          <td colSpan={10} className="px-4 py-2 text-xs text-gray-600 border-r border-gray-100">
                            <span className="inline-flex items-center gap-1.5 pl-6">
                              <span className="text-pink-400">↳</span>
                              {scheme.SchemeName || 'Unspecified scheme'}
                            </span>
                          </td>
                          <td className="px-4 py-2 border-r border-gray-100">
                            <span className="inline-flex px-3 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                              {scheme.total_open || 0}
                            </span>
                          </td>
                          <td className="px-4 py-2 border-r border-gray-100">
                            <span className="inline-flex px-3 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                              {scheme.total_br || 0}
                            </span>
                          </td>
                          <td className="px-4 py-2 border-r border-gray-100">
                            <span className="inline-flex px-3 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                              {scheme.total_ds || 0}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <span className="inline-flex px-3 py-0.5 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                              {scheme.total_aborted || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-4xl">📊</span>
                      <p className="text-sm">No HD data found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {sortedAndFilteredData.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-gray-500">
              Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(currentPage * itemsPerPage, sortedAndFilteredData.length)}</span> of{' '}
              <span className="font-medium">{sortedAndFilteredData.length}</span> entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-1 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
              >
                Previous
              </button>
              <span className="px-4 py-1 text-sm text-gray-600 font-medium">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-1 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Popup */}
      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default HDPerformanceDashboard;